import React from 'react'
import { Flex, TextVariant, Text, Wallet, RefreshIcon } from '@mprolab/uikit'
import { ethers } from 'ethers'
import styled, { useTheme } from 'styled-components'
import UseAnimations from 'react-useanimations'
import loading from 'react-useanimations/lib/loading'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import type { SwapToken } from 'utils/swap/types'

/**
 * The row above each amount field: what it is for, and what the wallet holds.
 *
 * Restored from the Swap tab this page used to carry, so the two tabs of the
 * exchange read as one screen rather than two products. Only its data source
 * changed — the balance is a `bigint` from chain now instead of a formatted
 * string from a store.
 */
const RefreshWrapper = styled(Flex)<{ refresh: boolean }>`
  cursor: pointer;
  animation: ${({ refresh }) => (refresh ? 'rotation' : 'none')} 1s;
  @keyframes rotation {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(359deg);
    }
  }
`

interface TokenBalancesProps {
  description: string
  token: SwapToken | null
  balance: bigint | null
  loading?: boolean
  onRefresh: () => void
}

const TokenBalances: React.FC<TokenBalancesProps> = ({
  description,
  token,
  balance,
  loading: isLoading,
  onRefresh
}) => {
  const { account } = useWeb3React()
  const { colors } = useTheme()

  const body = () => {
    if (!account || !token) return <></>
    if (isLoading || balance === null) {
      return (
        <Flex minWidth="40px" justifyContent="center">
          <UseAnimations animation={loading} size={14} strokeColor={colors.white3} />
        </Flex>
      )
    }
    const formatted = Number(
      ethers.utils.formatUnits(balance.toString(), token.decimals)
    ).toLocaleString(undefined, { maximumFractionDigits: 6 })
    return (
      <Flex gap="8px" alignItems="center">
        <Wallet width={16} height={16} color={colors.gray3} />
        <Text variant={TextVariant.TEXT_XS_BOLD} color={colors.gray3}>
          {`${formatted} ${token.symbol}`}
        </Text>
        <RefreshWrapper refresh={Boolean(isLoading)} onClick={onRefresh}>
          <RefreshIcon />
        </RefreshWrapper>
      </Flex>
    )
  }

  return (
    <Flex fullWidth justifyContent="space-between">
      <Text variant={TextVariant.TEXT_XS_BOLD}>{description}</Text>
      {body()}
    </Flex>
  )
}

export default TokenBalances
